import { Controller, FieldValues } from 'react-hook-form';
import { Label } from '../text-area/app-text-area';
import {
  CursorPaginatedOptionRenderProps,
  CursorPaginatedPopupRenderProps,
  CursorPaginatedSelectProps,
  CursorPaginatedTagRenderProps,
} from './cursor-pagnated-select.types';
import { Select, Spin } from 'antd';
import { forwardRef, LegacyRef, Ref } from 'react';

export const TagRender = (menu: CursorPaginatedTagRenderProps) => {
  const { label, closable, onClose } = menu;
  return (
    <div className="flex items-center gap-1 ml-1 mt-1 px-2 py-0.5 text-sm font-normal rounded-md bg-gray-200 text-text-700">
      <span>{label}</span>
      {closable && (
        <span onClick={onClose} className="cursor-pointer">
          ×
        </span>
      )}
    </div>
  );
};

export const OptionRender: CursorPaginatedOptionRenderProps =
  (hasNextPage, totalCount, loadMoreRef) =>
  (menu, { index }) => (
    <>
      {menu.data.label}

      {/* Append Spinner at the last label so that next page data can be fetched */}
      {index === totalCount - 1 && hasNextPage && (
        <div ref={loadMoreRef} className="p-2 text-center text-gray-500">
          <Spin size="small" />
        </div>
      )}
    </>
  );

export const PopupRender: CursorPaginatedPopupRenderProps = (menu, loading) => {
  if (loading) {
    return (
      <div className="py-6 text-center text-gray-500">
        <Spin size="small" />
      </div>
    );
  }

  return menu;
};

const CursorPaginatedInner = <TFieldValues extends FieldValues>(
  {
    control,
    name,
    label,
    suffixLabel,
    required,
    cursor,
    loadMoreRef,
    options,
    mode,
    loading,
    setSearchQuery,
    ...restProps
  }: CursorPaginatedSelectProps<TFieldValues>,
  ref: LegacyRef<React.ComponentRef<typeof Select>>
) => {
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <Label suffixLabel={suffixLabel} required={required}>
        {label}
      </Label>

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div className="w-full flex flex-col gap-1.5">
            <Select
              ref={ref}
              value={value}
              onChange={(option) => {
                onChange(option ?? null);
                setSearchQuery?.(''); // clear input on select/deselect
              }}
              onSearch={setSearchQuery}
              options={options}
              mode={mode}
              className={`min-w-full rounded-xl [&_.ant-select-selection-placeholder]:font-normal ${!error && 'mb-5'}`}
              size="large"
              filterOption={false}
              status={error ? 'error' : ''}
              tagRender={TagRender}
              optionRender={OptionRender(!!cursor?.hasNextPage, options?.length || 0, loadMoreRef)}
              popupRender={(menu) => PopupRender(menu, !!loading)}
              {...restProps}
            />

            {error && <p className="text-sm font-normal text-red-500">{error.message}</p>}
          </div>
        )}
      />
    </div>
  );
};

export const CursorPaginatedSelect = forwardRef(CursorPaginatedInner) as <T extends FieldValues>(
  props: CursorPaginatedSelectProps<T> & { ref?: Ref<React.ComponentRef<typeof Select>> }
) => ReturnType<typeof CursorPaginatedInner>;
